import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { hub } from "@/lib/hub-client";
import { formatTokens, relativeTime } from "@/lib/utils";

interface AuthorizedApp {
  id: string;
  partner_id: string;
  partner_name: string;
  partner_slug: string;
  max_monthly_tokens: number;
  per_call_cap: number;
  scope: string;
  authorized_at: string;
  status: "active" | "revoked";
  spent_this_month: number;
}

export function ConnectedAppsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["authorized-apps"],
    queryFn: async () => {
      const res = await hub.GET<{ items: AuthorizedApp[] }>("/v1/me/authorized-apps");
      if (!res.data) throw new Error("fetch failed");
      return res.data.items;
    },
  });

  const revoke = useMutation({
    mutationFn: async (partnerId: string) => {
      const res = await hub.DELETE(`/v1/me/authorized-apps/${partnerId}`);
      if (!res.response.ok) throw new Error("revoke failed");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["authorized-apps"] }),
  });

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <Link to="/settings" className="text-sm text-neutral-500 hover:underline">
        ← Settings
      </Link>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight">Connected apps</h1>
      <p className="mt-2 text-sm text-neutral-500">
        Third-party apps you've authorized to spend your AppSumo Plus tokens.
        Revoke anytime — in-flight holds complete on their own schedule; no
        new charges can happen after revocation.
      </p>

      {isLoading ? (
        <div className="mt-8 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-neutral-100 dark:bg-neutral-900" />
          ))}
        </div>
      ) : error ? (
        <p className="mt-8 text-sm text-red-500">{(error as Error).message}</p>
      ) : (data?.length ?? 0) === 0 ? (
        <p className="mt-12 rounded-lg border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500 dark:border-neutral-700">
          You haven't authorized any third-party apps yet.
        </p>
      ) : (
        <ul className="mt-8 space-y-4">
          {data!
            .filter((app) => app.status === "active")
            .map((app) => (
              <li
                key={app.id}
                className="rounded-xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-950"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-lg font-semibold">{app.partner_name}</p>
                    <p className="text-xs text-neutral-500">
                      Authorized {relativeTime(app.authorized_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (confirm(`Revoke ${app.partner_name}'s access?`)) {
                        revoke.mutate(app.partner_id);
                      }
                    }}
                    disabled={revoke.isPending}
                    className="rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:bg-red-950 dark:text-red-300"
                  >
                    Revoke
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                  <Stat
                    label="Monthly cap"
                    value={`${formatTokens(app.max_monthly_tokens)} tokens`}
                  />
                  <Stat
                    label="Per-call cap"
                    value={`${formatTokens(app.per_call_cap)} tokens`}
                  />
                  <Stat
                    label="Spent this month"
                    value={`${formatTokens(app.spent_this_month)} tokens`}
                    highlight={app.spent_this_month > 0.8 * app.max_monthly_tokens}
                  />
                </div>

                <p className="mt-3 text-xs text-neutral-500">
                  Scopes: <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] dark:bg-neutral-900">{app.scope}</code>
                </p>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div>
      <p className="text-xs text-neutral-500">{label}</p>
      <p className={highlight ? "mt-0.5 font-semibold text-amber-600 dark:text-amber-400" : "mt-0.5 font-semibold"}>
        {value}
      </p>
    </div>
  );
}
